import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, ListGroup, Spinner } from 'react-bootstrap';
import courseService from '@/shared/services/courseService';
import './CourseTypeahead.css';

// Search is served by the API, not filtered in the browser: the catalog is
// thousands of courses and the page never holds them all. Every keystroke
// would otherwise be a request, so input settles before one is sent.
const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS = 8;

function courseCode(course) {
    return `${course.subject} ${course.courseNumber}`;
}

// What tells one section of a course from another in the list: its section,
// CRN and meeting time, for whichever of them the record carries.
function sectionDetail(course) {
    const { days, startTime, endTime } = course.schedule || {};
    const time = [startTime, endTime].filter(Boolean).join('-');
    return [
        course.section && `Section ${course.section}`,
        course.crn && `CRN ${course.crn}`,
        [days, time].filter(Boolean).join(' '),
    ]
        .filter(Boolean)
        .join(' \u00b7 ');
}

/**
 * The one course search box, used wherever someone looks for a course, so
 * that finding one behaves the same everywhere.
 *
 * The box owns typing, debouncing and, when asked, its own suggestion list.
 * What happens as a result belongs to the page it sits on, which subscribes
 * to the events it needs:
 *
 * - `onSearch(text)`: the settled search text, once typing pauses. A page
 *   that already shows results, such as the catalog grid, filters on this and
 *   turns suggestions off, since a dropdown would repeat the cards beneath it.
 * - `onSelect(course)`: a course chosen from the suggestion list. A page with
 *   nothing else to show results in, such as the start of an application,
 *   keeps suggestions on and acts on the choice.
 * - `onQueryChange(text)`: every edit, immediately, for a page that must react
 *   before the text settles (clearing a choice the reader has typed past).
 *
 * `onSelect` receives the whole course object rather than an id: callers need
 * the code and title to show what was chosen, and re-fetching to display what
 * the user just picked is a request for data already in hand.
 */
function CourseTypeahead({
    termCode,
    onSelect = undefined,
    onSearch = undefined,
    onQueryChange = undefined,
    showSuggestions = true,
    placeholder = 'Search by course code or title',
    label = 'Find your course',
    autoFocus = false,
    initialQuery = '',
}) {
    const [query, setQuery] = useState(initialQuery || '');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeIndex, setActiveIndex] = useState(-1);

    const inputId = useId();
    const listId = `${inputId}-results`;
    const containerRef = useRef(null);
    // Responses can land out of order; only the newest query may render.
    const latestQueryRef = useRef('');
    // The text a selection just wrote into the box. Choosing a course fills
    // the input with its code AND title, which is not a query anyone typed
    // and matches nothing: the code branch wants a bare code, and no title
    // contains its own code. Searched, it answers "No courses match CHE 1800
    // General Chemistry I" a quarter second after the user picked exactly
    // that course, in a panel that covers the button they are reaching for.
    const committedLabelRef = useRef('');
    // Held in a ref so a host passing an inline function, which is a new
    // function on every host render, does not restart the debounce while
    // someone is still typing.
    const onSearchRef = useRef(onSearch);
    onSearchRef.current = onSearch;
    // The last text reported, so an edit that settles back to the same search
    // (a trailing space, a letter typed and deleted) does not ask the host to
    // fetch the same results again. Starts at the initial text so mounting is
    // not reported as a search.
    const lastSearchRef = useRef((initialQuery || '').trim());

    useEffect(() => {
        const trimmed = query.trim();
        // A selection writes a label, not a query; reporting it would filter a
        // host's results by text that matches nothing. Checked only when a
        // label exists: the ref is '' until something is chosen, so comparing
        // unconditionally swallows a cleared box and the host's filter never
        // lifts.
        if (committedLabelRef.current && trimmed === committedLabelRef.current)
            return undefined;
        if (trimmed === lastSearchRef.current) return undefined;
        const timer = setTimeout(() => {
            lastSearchRef.current = trimmed;
            if (onSearchRef.current) onSearchRef.current(trimmed);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        const trimmed = query.trim();
        latestQueryRef.current = trimmed;

        // Without suggestions the box only reports what is typed; it has no
        // list to fill, so it asks the server for nothing.
        if (!showSuggestions) return undefined;

        if (trimmed === committedLabelRef.current) {
            setResults([]);
            setLoading(false);
            setError(null);
            return undefined;
        }

        if (trimmed.length < MIN_QUERY_LENGTH) {
            setResults([]);
            setLoading(false);
            setError(null);
            return undefined;
        }

        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const data = await courseService.searchCoursesByText(
                    termCode,
                    trimmed,
                    { limit: MAX_RESULTS }
                );
                if (latestQueryRef.current !== trimmed) return;
                setResults(data.courses || []);
                setError(null);
                setOpen(true);
                setActiveIndex(-1);
            } catch (_err) {
                if (latestQueryRef.current !== trimmed) return;
                setResults([]);
                setError('Could not search courses. Try again.');
            } finally {
                if (latestQueryRef.current === trimmed) setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [query, termCode, showSuggestions]);

    // A click outside closes the list. Without this the results stay open over
    // whatever the user moved on to.
    useEffect(() => {
        function handleOutside(event) {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, []);

    const choose = useCallback(
        (course) => {
            const label = `${courseCode(course)} ${course.title}${course.section ? `, section ${course.section}` : ''}`;
            committedLabelRef.current = label;
            setQuery(label);
            setOpen(false);
            setActiveIndex(-1);
            if (onSelect) onSelect(course);
        },
        [onSelect]
    );

    const handleKeyDown = (event) => {
        if (!open || results.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((i) => (i + 1) % results.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
        } else if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            choose(results[activeIndex]);
        } else if (event.key === 'Escape') {
            setOpen(false);
            setActiveIndex(-1);
        }
    };

    const showEmpty =
        showSuggestions &&
        open &&
        !loading &&
        !error &&
        results.length === 0 &&
        query.trim().length >= MIN_QUERY_LENGTH;

    return (
        <div className="position-relative" ref={containerRef}>
            <Form.Label htmlFor={inputId}>{label}</Form.Label>
            <div className="position-relative">
                <Form.Control
                    id={inputId}
                    type="search"
                    autoComplete="off"
                    {...(showSuggestions
                        ? {
                              role: 'combobox',
                              'aria-expanded': open,
                              'aria-controls': listId,
                              'aria-autocomplete': 'list',
                              'aria-activedescendant':
                                  activeIndex >= 0
                                      ? `${listId}-option-${activeIndex}`
                                      : undefined,
                          }
                        : {})}
                    autoFocus={autoFocus}
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => {
                        // Editing the committed text makes it a query again.
                        committedLabelRef.current = '';
                        setQuery(e.target.value);
                        setOpen(true);
                        if (onQueryChange) onQueryChange(e.target.value);
                    }}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    onKeyDown={handleKeyDown}
                />
                {loading && (
                    <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                        className="position-absolute top-50 end-0 translate-middle-y me-3"
                    >
                        <span className="visually-hidden">
                            Searching courses
                        </span>
                    </Spinner>
                )}
            </div>

            {error && (
                <div className="text-danger small mt-1" role="alert">
                    {error}
                </div>
            )}

            {/* A screen reader is told how many matches there are; sighted
                users see the list appear. A page showing its own results
                announces them itself. */}
            {showSuggestions && (
                <div className="visually-hidden" aria-live="polite">
                    {open && results.length > 0
                        ? `${results.length} course${results.length === 1 ? '' : 's'} found`
                        : ''}
                </div>
            )}

            {open && results.length > 0 && (
                <ListGroup
                    id={listId}
                    role="listbox"
                    className="position-absolute w-100 shadow-sm course-typeahead-results"
                >
                    {results.map((course, index) => (
                        <ListGroup.Item
                            key={course._id}
                            id={`${listId}-option-${index}`}
                            role="option"
                            aria-selected={index === activeIndex}
                            action
                            active={index === activeIndex}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => choose(course)}
                        >
                            <span className="fw-semibold">
                                {courseCode(course)}
                            </span>{' '}
                            <span>{course.title}</span>
                            {sectionDetail(course) && (
                                <div className="small">
                                    {sectionDetail(course)}
                                </div>
                            )}
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}

            {showEmpty && (
                <ListGroup className="position-absolute w-100 shadow-sm course-typeahead-results">
                    <ListGroup.Item className="text-muted">
                        No courses match “{query.trim()}”. Try a course code
                        like CHE 1800, or part of the title.
                    </ListGroup.Item>
                </ListGroup>
            )}
        </div>
    );
}

CourseTypeahead.propTypes = {
    /** Term whose catalog the suggestions come from. */
    termCode: PropTypes.string.isRequired,
    /** Called with the whole course chosen from the suggestion list. */
    onSelect: PropTypes.func,
    /** Called with the trimmed search text once typing settles. */
    onSearch: PropTypes.func,
    /** Called with the raw text on every edit, before it settles. */
    onQueryChange: PropTypes.func,
    /** Whether the box shows its own suggestion list. */
    showSuggestions: PropTypes.bool,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    autoFocus: PropTypes.bool,
    initialQuery: PropTypes.string,
};

export default CourseTypeahead;
